import React, { useState, useEffect, useMemo } from 'react';
import IconSvg from '../../../img/trip_planner/steps/step_4.svg';
import useQuestionTrip from "../../../../../hooks/useQuestionTrip";

interface Interest {
    id: number;
    name: string;
    description?: string | null;
    subAnswers?: Interest[];
}

interface Step4DestinationProps {
    onStepValidation: (isValid: boolean) => void;
    onUpdateTripProfile: (selectedIds: number[]) => void;
    initialSelectedIds?: number[];
}

const Step4Destination = ({ onStepValidation, onUpdateTripProfile, initialSelectedIds = [] }: Step4DestinationProps) => {
    const { questionsTrip } = useQuestionTrip();
    const [selectedInterests, setSelectedInterests] = useState<number[]>([]);
    const [isInitialized, setIsInitialized] = useState(false);

    // Initialize with existing answerIds when editing a trip
    useEffect(() => {
        if (!isInitialized && initialSelectedIds.length > 0) {
            setSelectedInterests(initialSelectedIds);
            setIsInitialized(true);
        }
    }, [initialSelectedIds, isInitialized]);

    const handleInterestClick = (id: number) => {
        if (!isInitialized) {
            setIsInitialized(true);
        }
        setSelectedInterests(prev => {
            if (prev.includes(id)) {
                return prev.filter(item => item !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    useEffect(() => {
        onStepValidation(selectedInterests.length > 0);
    }, [selectedInterests, onStepValidation]);

    // Only update trip profile after initialization to prevent wiping existing data
    useEffect(() => {
        if (!isInitialized) return;

        const debouncedUpdate = setTimeout(() => {
            onUpdateTripProfile(selectedInterests);
        }, 100);

        return () => clearTimeout(debouncedUpdate);
    }, [selectedInterests, onUpdateTripProfile, isInitialized]);

    const renderInterestButton = (interest: Interest) => {
        const isSelected = selectedInterests.includes(interest.id);
        return (
            <button
                key={interest.id}
                onClick={() => handleInterestClick(interest.id)}
                className={`px-6 py-3 rounded-full border ${
                    isSelected
                        ? 'border-primary-color bg-primary-color-50 text-primary-color'
                        : 'border-gray-300 hover:border-gray-400'
                } transition-all duration-200 ease-in-out`}
            >
                {interest.name}
            </button>
        );
    };

    const interests = useMemo(() => {
        const question = questionsTrip?.find(q => q.id === 6);
        if (!question) return [];
        
        const allInterests: Interest[] = [];
        
        question.answers.forEach(answer => {
            if (answer.subAnswers?.length) {
                allInterests.push(...answer.subAnswers);
            } else {
                allInterests.push(answer);
            }
        });
        
        return allInterests;
    }, [questionsTrip]);

    return (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <div className="w-20-h mx-auto mb-4 relative">
                    <img
                        src={IconSvg}
                        alt="What are you interested in?"
                        className="w-full h-full object-contain"
                    />
                    <div className="absolute inset-0" style={{ backgroundColor: 'var(--primary-color)', mixBlendMode: 'lighten' }} />
                </div>
                <h2 className="text-2xl font-semibold mb-2 syne-fm">Tell us what you're interested in?</h2>
            </div>

            <div className="grid grid-cols-2 md:flex md:flex-wrap gap-3 md:justify-center">
                {interests.map(interest => renderInterestButton(interest))}
            </div>
        </div>
    );
};

export default Step4Destination;
